import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AuthShell from '../../components/marketing/AuthShell';
import {
  useCreateFirm, useFirmSearch, useRefreshSession, useRequestFirmMembership, useSignup,
} from '../../api/hooks';
import { useAdoptSession, homeOf } from '../../hooks';
import {
  DEFAULT_ROLE, FIRM_TYPES, FIRM_TYPE_LABEL, INDIVIDUAL_SEAT, PROFILE_KEYS, PROFILE_TITLES, ROLE_OF_SEAT, ROUTES,
} from '../../config/navigation';
import { isReachablePhone, plainError, plainPhone } from '../../lib/format';

/**
 * Create a workspace, in two steps: the account, then the firm it belongs to.
 *
 * Step two is where someone signs up as an employee of a firm that is already
 * here: they find it by name and ask to join, and the owner approves. Founders
 * take the other side of the step and set the firm up, approved at once. It
 * cannot be folded into step one — searching for a firm and creating one are
 * both authenticated calls, so they need the account's token, which is why the
 * session is adopted between the steps rather than navigated into.
 *
 * The firm is not optional decoration: every firm-side write (adding a client
 * above all) is refused until a membership is APPROVED, so skipping the step
 * leads to Firm & team, which asks the same question again.
 *
 * The role choice is the app's own seat: it previews the desk until the firm
 * membership puts the real role on the token, and it is the role the firm
 * membership is asked for.
 *
 * An email and a phone number are required on both sides of the step — the
 * person's own, and the firm's — because everything either side does next is
 * somebody reaching out: a firm's owner answering a join request, and us
 * reaching a firm about its registration or its plan.
 */

const NOTES = {
  starter: 'Starting on Starter — ₹1,999/mo after your 14-day free trial',
  professional: 'Starting a 14-day Professional trial · no card required',
  organization: "Organization tier — we'll reach out within one business day to scope seats & integrations",
  enterprise: "Enterprise — tell us about your entities and residency needs; we'll design the deployment with you",
};

/** Seat → the registration credential that seat carries. */
const REG = {
  mfd: ['ARN number', 'ARN-______'],
  pm: ['SEBI MF registration (your fund house)', 'MF/___/__/__'],
  cio: ['SEBI MF registration', 'MF/___/__/__'],
  pms: ['SEBI PMS registration', 'INP__________'],
};

const AUM_BANDS = ['Under ₹10 Cr', '₹10–100 Cr', '₹100–1,000 Cr', '₹1,000 Cr+'];

const hint = { fontSize: 12.5, color: 'var(--ink3)', lineHeight: 1.55 };
const link = { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 13, color: 'var(--ink3)', cursor: 'pointer', textDecoration: 'underline' };

/** One of the two ways into a firm, as a tile the whole card wide. */
function Choice({ title, blurb, tag, onClick }) {
  return (
    <button type="button" className="lx-choice" onClick={onClick}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
        <b style={{ fontSize: 15 }}>{title}</b>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', color: 'var(--brand)' }}>{tag}</span>
      </div>
      <div style={{ ...hint, marginTop: 5 }}>{blurb}</div>
    </button>
  );
}

/** Where the person is in the two steps, above the card's heading. */
function StepBar({ step }) {
  return (
    <div className="lx-stepbar" role="img" aria-label={`Step ${step} of 2`}>
      <span className={step >= 1 ? 'on' : ''}><i />Account</span>
      <span className={step >= 2 ? 'on' : ''}><i />Firm</span>
    </div>
  );
}

/** The seat select every step here shares: the firm role is asked as a seat. */
function SeatField({ value, onChange }) {
  return (
    <div className="field">
      <label>Your role at the firm</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {PROFILE_KEYS.map((k) => <option key={k} value={k}>{PROFILE_TITLES[k]}</option>)}
      </select>
    </div>
  );
}

/**
 * Step two — the firm. Found one (owner, approved at once) or ask to join one
 * (employee, the owner approves). `registration` is the ARN / SEBI number from
 * step one: the backend has no field of its own for it, so on a firm the person
 * founds it is kept in the firm's description rather than dropped.
 */
function FirmStep({ defaultSeat, registration, email, phone }) {
  const navigate = useNavigate();
  const refreshSession = useRefreshSession();
  const [createFirm, creating] = useCreateFirm();
  const [requestJoin, requesting] = useRequestFirmMembership();
  const [mode, setMode] = useState(null);
  const [seat, setSeat] = useState(defaultSeat);
  /* A solo MFD is a firm of one, so the founder's own number is offered as the
     firm's — theirs to change, never a blank to retype. */
  const [firm, setFirm] = useState({ name: '', email: '', phone: phone || '', type: FIRM_TYPES[0][0] });
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [sentTo, setSentTo] = useState(null);
  const [failure, setFailure] = useState(null);
  const search = useFirmSearch(query);

  /* Ask once typing pauses, not on every keystroke. */
  useEffect(() => {
    const t = setTimeout(() => setQuery(term), 250);
    return () => clearTimeout(t);
  }, [term]);

  const set = (k) => (e) => setFirm({ ...firm, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    setFailure(null);
    try {
      await createFirm({
        variables: {
          input: {
            name: firm.name.trim(),
            email: firm.email.trim(),
            organizationType: firm.type,
            phoneNumber: plainPhone(firm.phone),
            firmRole: ROLE_OF_SEAT[seat],
            description: registration ? `${FIRM_TYPE_LABEL[firm.type] || firm.type} · ${registration}` : null,
          },
        },
      });
      /* The founder is approved at once, so a fresh token carries the role —
         take it before the desk opens, or the seat is only this device's. */
      const assigned = await refreshSession().catch(() => null);
      navigate(homeOf(assigned || seat), { replace: true });
    } catch (err) {
      setFailure(plainError(err, 'Could not create the firm'));
    }
  };

  const join = async (org) => {
    setFailure(null);
    try {
      await requestJoin({ variables: { organizationId: org.organizationId, firmRole: ROLE_OF_SEAT[seat] } });
      setSentTo(org.name);
    } catch (err) {
      setFailure(plainError(err, 'Could not send the request'));
    }
  };

  if (sentTo) {
    return (
      <>
        <h1>Request sent</h1>
        <div className="sub">
          {sentTo} has your request to join as {PROFILE_TITLES[seat]}. The firm’s owner approves it;
          your desk opens on that seat as soon as they do.
        </div>
        <button className="btn pri lg" style={{ width: '100%', marginTop: 24 }} type="button" onClick={() => navigate(ROUTES.team, { replace: true })}>
          Go to Firm &amp; team →
        </button>
        <p style={{ ...hint, marginTop: 14, textAlign: 'center' }}>
          Until it is approved, client data and client-facing features stay closed.
        </p>
      </>
    );
  }

  return (
    <>
      <h1>{mode === 'own' ? 'Set up your firm' : mode === 'join' ? 'Find your firm' : 'Your firm'}</h1>
      <div className="sub">
        {mode === 'own' && 'You become its owner and approve everyone who asks to join.'}
        {mode === 'join' && 'Ask to join it as an employee — the owner approves your seat.'}
        {!mode && 'A desk belongs to a firm. Set yours up, or join the one you work at.'}
      </div>

      {!mode && (
        <>
          <Choice
            title="I’m setting up my firm"
            tag="APPROVED AT ONCE"
            blurb="For the owner of the practice — a solo MFD is a firm of one."
            onClick={() => setMode('own')}
          />
          <Choice
            title="I work at a firm that’s already here"
            tag="OWNER APPROVES"
            blurb="Find your firm by name and ask to join it as its employee."
            onClick={() => { setMode('join'); setFirm({ ...firm, email: '' }); }}
          />
          <p style={{ marginTop: 18, textAlign: 'center' }}>
            <button type="button" style={link} onClick={() => navigate(ROUTES.team, { replace: true })}>
              I’ll do this later
            </button>
          </p>
          <p style={{ ...hint, marginTop: 10, textAlign: 'center' }}>
            Adding clients, and everything else your firm shares, needs an approved membership.
          </p>
        </>
      )}

      {mode === 'own' && (
        <form onSubmit={create}>
          <div className="field">
            <label>Firm name</label>
            <input placeholder="Acme Wealth" required value={firm.name} onChange={set('name')} />
          </div>
          <div className="field">
            <label>Firm email</label>
            <input
              type="email"
              placeholder={email ? `desk@${(email.split('@')[1] || 'yourfirm.in')}` : 'desk@yourfirm.in'}
              required
              value={firm.email}
              onChange={set('email')}
            />
          </div>
          <div className="field">
            <label>Firm phone</label>
            <input
              type="tel"
              placeholder="+91 98765 43210"
              required
              value={firm.phone}
              onChange={set('phone')}
            />
            {firm.phone && !isReachablePhone(firm.phone) && (
              <p style={{ ...hint, marginTop: 6 }}>That looks short — give the number with its area or country code.</p>
            )}
          </div>
          <div className="field">
            <label>Firm type</label>
            <select value={firm.type} onChange={set('type')}>
              {FIRM_TYPES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>
          <SeatField value={seat} onChange={setSeat} />
          <button
            className="btn pri lg"
            style={{ width: '100%', marginTop: 22 }}
            type="submit"
            disabled={creating.loading || !isReachablePhone(firm.phone)}
          >
            {creating.loading ? 'Creating…' : 'Create firm →'}
          </button>
        </form>
      )}

      {mode === 'join' && (
        <>
          <div className="field">
            <label>Firm name</label>
            <input placeholder="Type at least 2 letters" value={term} onChange={(e) => setTerm(e.target.value)} />
          </div>
          <SeatField value={seat} onChange={setSeat} />
          <div style={{ marginTop: 16 }}>
            {query.trim().length < 2 ? (
              <p style={hint}>
                Colleagues find the firm by the name its owner set up. Your name and email go with
                the request, so the owner knows who is asking.
              </p>
            ) : search.loading ? (
              <p style={hint}>Looking…</p>
            ) : search.firms.length ? (
              search.firms.map((f) => (
                <div
                  key={f.organizationId}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                    padding: '10px 0', borderBottom: '1px solid var(--line)', fontSize: 13.5,
                  }}
                >
                  <span>
                    <b>{f.name}</b>
                    {f.organizationType && (
                      <span style={{ color: 'var(--ink3)' }}> · {FIRM_TYPE_LABEL[f.organizationType] || f.organizationType}</span>
                    )}
                  </span>
                  <button className="btn" type="button" onClick={() => join(f)} disabled={requesting.loading}>
                    {requesting.loading ? 'Sending…' : 'Request to join'}
                  </button>
                </div>
              ))
            ) : (
              <p style={hint}>
                No firm by that name. Check the spelling, or ask its owner to set the firm up first —
                you can also set it up yourself.
              </p>
            )}
          </div>
        </>
      )}

      {failure && <div className="auth-msg bad">{failure}</div>}

      {mode && (
        <p style={{ marginTop: 18, textAlign: 'center' }}>
          <button type="button" style={link} onClick={() => { setMode(null); setFailure(null); }}>
            ← Back
          </button>
        </p>
      )}
    </>
  );
}

export default function Signup() {
  const [params, setParams] = useSearchParams();
  const plan = params.get('plan');
  /* Which step is on screen lives in the URL, not in state: the account exists
     the moment step one succeeds, and a reload that went back to step one left
     the person retyping details into a form that could only answer "email
     already registered". */
  const onFirmStep = params.get('step') === 'firm';
  /* The seat the session is running as — what step one recorded. Read from the
     store so a reload on step two still knows it. */
  const sessionSeat = useSelector((st) => st.ui.role);
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '', reg: '', aum: AUM_BANDS[0], password: '' });
  const [failure, setFailure] = useState(null);
  const [notice, setNotice] = useState(null);
  const [signup, { loading }] = useSignup();
  const adopt = useAdoptSession();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const reg = REG[form.role];

  const submit = async (e) => {
    e.preventDefault();
    setFailure(null);
    setNotice(null);
    try {
      const { data } = await signup({
        variables: {
          user: {
            email: form.email.trim(),
            password: form.password,
            name: form.name.trim() || null,
            /* Required by the form: a firm's owner reviewing a join request,
               and we ourselves, need a way to reach this person. */
            phoneNumber: plainPhone(form.phone),
          },
        },
      });
      const auth = data?.signupUser;
      if (!auth?.success) throw new Error(auth?.message || 'Sign up failed');
      if (!auth.authToken) {
        setNotice('Account created. Sign in to continue.');
        return;
      }
      const seat = adopt(auth, form.role || DEFAULT_ROLE);
      /* An individual has no firm to found or join: their desk opens now. */
      if (seat === INDIVIDUAL_SEAT) {
        navigate(homeOf(INDIVIDUAL_SEAT), { replace: true });
        return;
      }
      const next = new URLSearchParams(params);
      next.set('step', 'firm');
      setParams(next, { replace: true });
    } catch (err) {
      setFailure(plainError(err, 'Sign up failed'));
    }
  };

  return (
    <AuthShell
      aside={(
        <>
          <div className="lx-auth-say">
            <h2>90 seconds from now, you’ll see things in a portfolio <em>you hadn’t thought to ask.</em></h2>
            <p className="read">Sign up, upload one CAS, and get the verdict: the overlap nobody noticed, the factor
              bet nobody chose, the one change that most improves it — with the math one click beneath.</p>
            <ol className="lx-auth-steps">
              <li className={onFirmStep ? 'done' : 'on'}><b>Create your account</b><span>Name, work email, phone and your role</span></li>
              <li className={onFirmStep ? 'on' : ''}><b>Set up or join your firm</b><span>Owners are approved at once; colleagues ask to join</span></li>
              <li><b>Upload one CAS</b><span>Previewed before anything is booked, then the verdict</span></li>
            </ol>
            <ul className="lx-auth-ticks">
              <li><b>14-day Professional trial</b> — full features, no card</li>
              <li><b>No commission share</b> — a flat subscription, ever</li>
            </ul>
          </div>
          <figure className="lx-auth-quote">
            <blockquote>
              “The prospect uploaded his CAS on my phone during the meeting. By the time coffee arrived, we’d
              found 41% overlap he was paying twice for. Signed him that week.”
            </blockquote>
            <figcaption>— MFD member, Pune</figcaption>
          </figure>
        </>
      )}
    >
      <StepBar step={onFirmStep ? 2 : 1} />
      {onFirmStep ? (
        <FirmStep
          defaultSeat={[form.role, sessionSeat].find((k) => PROFILE_KEYS.includes(k)) || PROFILE_KEYS[0]}
          registration={form.reg.trim() || null}
          email={form.email.trim()}
          phone={plainPhone(form.phone)}
        />
      ) : (
        <>
          <h1>Create your workspace</h1>
          <div className="sub">{NOTES[plan] || NOTES.professional}</div>
          <form onSubmit={submit}>
            <div className="field">
              <label>Full name</label>
              {/* Generic, like the email field beside it: a real person's name as
                  the placeholder reads as someone else's account rather than as an
                  example of the shape wanted. */}
              <input placeholder="Your full name" required value={form.name} onChange={set('name')} />
            </div>
            <div className="field">
              <label>Work email</label>
              <input type="email" autoComplete="email" placeholder="you@yourfirm.com" required value={form.email} onChange={set('email')} />
            </div>
            <div className="field">
              <label>Phone</label>
              <input type="tel" autoComplete="tel" placeholder="+91 98765 43210" required value={form.phone} onChange={set('phone')} />
            </div>
            <div className="field">
              <label>I am a…</label>
              <select required value={form.role} onChange={set('role')}>
                <option value="">Choose your role</option>
                {PROFILE_KEYS.map((k) => (
                  <option key={k} value={k}>{PROFILE_TITLES[k]}</option>
                ))}
                <option value={INDIVIDUAL_SEAT}>{PROFILE_TITLES[INDIVIDUAL_SEAT]} — I invest my own money</option>
              </select>
            </div>
            {reg && (
              <div className="field">
                <label>{reg[0]}</label>
                <input placeholder={reg[1]} value={form.reg} onChange={set('reg')} />
              </div>
            )}
            <div className="field">
              <label>Approx. assets you manage / advise</label>
              <select value={form.aum} onChange={set('aum')}>
                {AUM_BANDS.map((band) => <option key={band}>{band}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Password</label>
              <input type="password" autoComplete="new-password" placeholder="8+ characters" minLength={8} required value={form.password} onChange={set('password')} />
            </div>
            <button
              className="btn pri lg"
              style={{ width: '100%', marginTop: 22 }}
              type="submit"
              disabled={loading || !isReachablePhone(form.phone)}
            >
              {loading ? 'Please wait…' : 'Continue — your firm →'}
            </button>
            {failure && <div className="auth-msg bad">{failure}</div>}
            {notice && <div className="auth-msg">{notice} <Link to="/login" style={{ color: 'var(--brand)', fontWeight: 700 }}>Log in</Link></div>}
            <p style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 14, textAlign: 'center' }}>
              Next you set up your firm, or ask to join the one you work at. By continuing you agree
              to our Terms and acknowledge our Privacy Policy. Your ARN/SEBI registration is
              recorded with your firm.
            </p>
          </form>
          <div className="auth-alt">Already a member? <Link to="/login">Log in</Link></div>
        </>
      )}
    </AuthShell>
  );
}
